/**
 * O BLOQUEIO NA PROPRIA TELA — quem esta com o ponto fechado para de operar sem perder
 * onde estava.
 *
 * Pedido do Ruan (11/09/2026): *"quando as meninas batem o ponto, o sistema abre varias
 * abas do ponto no navegador, sobrepondo as abas que ja estavam abertas com outras telas
 * do sistema"*. Nao abria aba nenhuma: cada aba VIVA era redirecionada pelo `api.ts` ao
 * levar o 403 da trava, e a tela dela morria junto. Medido no `access.log` de 9 dias
 * uteis: **137 telas sequestradas** (~15/dia), TODAS depois de uma batida de SAIDA — 23
 * vezes o Caixa, 23 o Atendimento, 15 Orcamentos, 14 Reposicoes e **7 vezes um orcamento
 * sendo digitado**. Nenhuma era recarga da propria tela de ponto.
 *
 * ⛔ **A trava do servidor NAO mudou** (decisao dele de 31/08, ver `ponto-presenca.ts`) —
 * `/api/*` segue recusado inteiro. O que mudou e' que a aba que ja tinha carregado tudo
 * **fica onde esta**, com este bloqueio por cima. O desvio de quem CHEGA sem ponto
 * continua sendo o `ExigirPontoDoDia` do App.tsx: la e' boot de verdade, a tela viraria
 * destrocos, e nao ha nada a preservar.
 *
 * ⚠️ **O titulo nao diz "voce bateu a saida"** de proposito: o
 * `ponto-fechamento.scheduler` fecha ponto sozinho as 23h, e a tela estaria afirmando um
 * gesto que ninguem fez — o mesmo cuidado que ja impediu um "Bom dia" fixo na
 * `PontoEntradaDoDia`.
 *
 * ⚠️ **Consequencia aceita e conhecida**: a aba que fica volta com a lista do momento em
 * que travou (o que chegou durante o almoco so aparece com F5). Antes ela voltava limpa
 * porque era recarregada a forca. E' o mesmo buraco ja anotado em `docs/pendencias.md`
 * sobre recarregar a lista ao reconectar — nao resolvido aqui de proposito.
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import { api } from '../lib/api';
import { EVENTO_PONTO_BLOQUEADO, guardarDestinoDoPonto } from '../lib/ponto-bloqueio';
import { useSocket } from '../lib/socket';

/** De quanto em quanto tempo a tela pergunta se a entrada voltou, enquanto bloqueada. */
const INTERVALO_CONFERE_MS = 6_000;

interface DiaPonto {
  /** 'entrada' = ausente (precisa bater), 'saida' = presenca VIVA. Espelha o GET /api/ponto/dia. */
  proxima_acao: 'entrada' | 'saida';
}

export function AvisoPontoFechado() {
  const { reconectar } = useSocket();
  const [bloqueado, setBloqueado] = useState(false);

  useEffect(() => {
    function aoBloquear() {
      setBloqueado(true);
    }
    window.addEventListener(EVENTO_PONTO_BLOQUEADO, aoBloquear);
    return () => window.removeEventListener(EVENTO_PONTO_BLOQUEADO, aoBloquear);
  }, []);

  /**
   * Enquanto bloqueado, pergunta ao proprio ponto se a entrada ja foi batida.
   *
   * ⚠️ `/api/ponto/dia` e' uma das rotas ISENTAS da trava (`PREFIXOS_ISENTOS` no
   * `exigir-ponto-do-dia.ts`) — se nao fosse, a pergunta seria recusada pela mesma trava
   * que ela existe pra observar, e o aviso nunca sairia da tela.
   *
   * ⚠️ O `visibilitychange` nao e' luxo: o navegador estrangula timer de aba em segundo
   * plano, e a aba que ela volta a olhar e' justamente a que precisa responder na hora.
   */
  const conferir = useCallback(async () => {
    try {
      const dia = await api.get<DiaPonto>('/api/ponto/dia');
      if (dia.proxima_acao === 'saida') {
        setBloqueado(false);
        reconectar();
      }
    } catch {
      // Falha de rede/banco nao destrava nada: quem destrava e' a entrada batida, e
      // insistir no proximo tique custa uma consulta indexada.
    }
  }, [reconectar]);

  useEffect(() => {
    if (!bloqueado) return;
    const id = window.setInterval(() => void conferir(), INTERVALO_CONFERE_MS);
    function aoVoltarPraAba() {
      if (document.visibilityState === 'visible') void conferir();
    }
    document.addEventListener('visibilitychange', aoVoltarPraAba);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', aoVoltarPraAba);
    };
  }, [bloqueado, conferir]);

  if (!bloqueado) return null;

  return createPortal(
    /*
      ⚠️ **z-index 1200, e nao o `z-[70]` que o CLAUDE.md pede pra modal de pagina**: este
      nao e' um modal da tela, e' um bloqueio do SISTEMA — ele tem que cobrir tambem o
      `.modal-overlay` da casa (1000) e o andar de quem abre de dentro de outro modal
      (1100). Quem bate a saida com um modal aberto na frente precisa ver isto, nao o
      modal.
      ⚠️ Fundo semi-transparente de proposito: a tela por baixo continua visivel, e e' ela
      que diz "nada do que voce estava fazendo se perdeu".
    */
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="aviso-ponto-titulo"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(0,0,0,0.55)',
      }}
    >
      {/* Overlay `fixed` nao herda as classes do tema — cor vai inline, em par CASADO
          (`--panel-bg` com `--text`), nunca misturando familias de variavel ([DA-678]). */}
      <div
        style={{
          width: 440,
          maxWidth: '92vw',
          borderRadius: 12,
          padding: 24,
          textAlign: 'center',
          boxShadow: '0 18px 50px rgba(0,0,0,0.45)',
          backgroundColor: 'var(--panel-bg)',
          color: 'var(--text)',
        }}
      >
        {/* Par fixo `#fde68a` + `#000` — o unico que o Ruan distingue com seguranca
            ([[user_daltonismo]]), e que funciona nos dois temas. */}
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            margin: '0 auto 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fde68a',
            color: '#000',
          }}
        >
          <Clock size={26} />
        </div>

        <div id="aviso-ponto-titulo" style={{ fontSize: 19, fontWeight: 700, marginBottom: 8 }}>
          Seu ponto esta fechado
        </div>

        <div style={{ fontSize: 14, lineHeight: 1.55, opacity: 0.9, marginBottom: 6 }}>
          O sistema fica bloqueado ate voce registrar a entrada de novo.
        </div>

        <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.75, marginBottom: 20 }}>
          Esta tela continua aberta aqui — nada do que voce estava fazendo foi perdido.
        </div>

        <button
          type="button"
          className="btn-primario"
          style={{ padding: '10px 22px', fontSize: 15, cursor: 'pointer' }}
          onClick={() => {
            // Guarda de onde ela saiu — a tela de ponto devolve pra ca depois da batida.
            guardarDestinoDoPonto(window.location.pathname + window.location.search);
            window.location.href = '/ponto/entrada-do-dia';
          }}
        >
          Ir bater o ponto
        </button>

        <div style={{ fontSize: 12, opacity: 0.6, marginTop: 14 }}>
          Bateu a entrada em outra aba? Este aviso some sozinho.
        </div>
      </div>
    </div>,
    document.body,
  );
}
